import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../assets/css/KnowledgeBase.css";
import "../../assets/css/Urls.css";
import axios from "axios";
import { RefreshCcw, FileText, Layers, AlertTriangle, Trash2 } from "lucide-react";
import { getProjectPid } from "../../utils/sessionProject";
import { toast } from 'react-toastify';
import DeleteConfirmModal from "../../components/DeleteConfirmModal";

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const BASE = process.env.REACT_APP_BASE_URL;

const KnowledgeBaseDetails = () => {
  const { doc_id } = useParams();
  const navigate = useNavigate();
  const projectPid = getProjectPid();

  const [activeTab, setActiveTab] = useState("content");

  // Current doc content from /get
  const [currentDocPid, setCurrentDocPid] = useState(doc_id);
  const [title, setTitle] = useState("");
  const [originalTitle, setOriginalTitle] = useState("");
  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [status, setStatus] = useState("draft");
  const [chunks, setChunks] = useState([]);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [docLoading, setDocLoading] = useState(true);

  // Save states
  const [savingTitle, setSavingTitle] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isLive = status === "live";

  // 1. Fetch current doc title+content from /get
  const fetchDoc = useCallback(async (docPid) => {
    setDocLoading(true);
    setTitle("");
    setContent("");
    setChunks([]);
    try {
      const res = await axios.post(
        `${BASE}/api/v1/knowledge-base/get`,
        { projectPid, docPid },
        { headers: getAuthHeader() }
      );
      const doc = res.data?.response || {};
      setCurrentDocPid(doc.docPid || docPid);
      const t = doc.title || "";
      const c = doc.content || "";
      setTitle(t);
      setOriginalTitle(t);
      setContent(c);
      setOriginalContent(c);
      setStatus(doc.status || "draft");
    } catch {
      setTitle("");
      setContent("");
      setStatus("draft");
    } finally {
      setDocLoading(false);
    }
  }, [projectPid]);

  // 2. Fetch chunks from /chunks
  const fetchChunks = useCallback(async (docPid) => {
    setChunksLoading(true);
    try {
      const res = await axios.post(
        `${BASE}/api/v1/knowledge-base/chunks`,
        { projectPid, docPid },
        { headers: getAuthHeader() }
      );
      const data = res.data?.response || {};
      setChunks(Array.isArray(data.chunks) ? data.chunks : []);
    } catch {
      setChunks([]);
    } finally {
      setChunksLoading(false);
    }
  }, [projectPid]);

  useEffect(() => {
    if (doc_id) {
      fetchDoc(doc_id);
      fetchChunks(doc_id);
    }
  }, [doc_id, fetchDoc, fetchChunks]);

  // Save title
  const handleSaveTitle = async () => {
    setSavingTitle(true);
    try {
      await axios.patch(
        `${BASE}/api/v1/knowledge-base/update`,
        { projectPid, docPid: currentDocPid, title, content },
        { headers: getAuthHeader() }
      );
      setOriginalTitle(title);
      toast.success("Title saved successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save title.");
    } finally {
      setSavingTitle(false);
    }
  };

  // Save content
  const handleSaveContent = async () => {
    setSavingContent(true);
    try {
      await axios.patch(
        `${BASE}/api/v1/knowledge-base/update`,
        { projectPid, docPid: currentDocPid, title, content },
        { headers: getAuthHeader() }
      );
      setOriginalContent(content);
      toast.success("Content saved successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save content.");
    } finally {
      setSavingContent(false);
    }
  };

  const handleReset = () => {
    setTitle(originalTitle);
    setContent(originalContent);
  };

  // Change status (draft<->ready, or live->draft after confirmation)
  const changeStatus = async (newStatus) => {
    setSavingStatus(true);
    try {
      await axios.patch(
        `${BASE}/api/v1/knowledge-base/status`,
        { projectPid, docPid: currentDocPid, status: newStatus },
        { headers: getAuthHeader() }
      );
      setStatus(newStatus);
      toast.success(`Document marked as ${newStatus}.`);
      if (newStatus === "draft") setChunks([]);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status.");
    } finally {
      setSavingStatus(false);
      setShowRevertConfirm(false);
    }
  };

  const handleStatusButtonClick = () => {
    if (status === "draft") {
      changeStatus("ready");
    } else if (status === "ready") {
      changeStatus("draft");
    } else if (status === "live") {
      setShowRevertConfirm(true);
    }
  };

  const handleDeleteDocument = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${BASE}/api/v1/knowledge-base/delete`, {
        data: { projectPid, docPid: currentDocPid },
        headers: getAuthHeader(),
      });
      toast.success("Document deleted successfully!");
      navigate("/knowledge-base");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete document.");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {showRevertConfirm && (
          <div
            style={{
              position: "fixed", inset: 0, zIndex: 2000,
              background: "rgba(15,23,42,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
            onClick={() => setShowRevertConfirm(false)}
          >
            <div
              style={{
                background: "#fff", borderRadius: 14, padding: "28px 28px 20px",
                maxWidth: 400, width: "90%", boxShadow: "0 8px 32px rgba(15,23,42,0.18)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h6 style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", margin: "0 0 8px" }}>
                Revert to draft?
              </h6>
              <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px" }}>
                This will delete all existing chunks for this document. Continue?
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  className="btn btn-light"
                  style={{ fontSize: 13, borderRadius: 8 }}
                  onClick={() => setShowRevertConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  style={{ fontSize: 13, borderRadius: 8 }}
                  onClick={() => changeStatus("draft")}
                  disabled={savingStatus}
                >
                  {savingStatus ? "Reverting..." : "Revert to Draft"}
                </button>
              </div>
            </div>
          </div>
        )}

        <DeleteConfirmModal
          show={showDeleteConfirm}
          type="document"
          value={title || currentDocPid}
          warning="This is irreversible — it also deletes the document's content, chunks, and original uploaded file (if any)."
          confirming={deleting}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteDocument}
        />

        {/* Tabs */}
        <section className="url-list-toolbar p-3 mb-3">
          <div className="kbd-tab-bar">
            <div className="kb-tabs">
              <button
                className={`kb-tab ${activeTab === "content" ? "active" : ""}`}
                onClick={() => setActiveTab("content")}
              >
                <FileText size={13} />
                Content
              </button>
              <button
                className={`kb-tab ${activeTab === "chunks" ? "active" : ""}`}
                onClick={() => setActiveTab("chunks")}
              >
                <Layers size={13} />
                Chunks
              </button>
            </div>
            {!docLoading && (
              <div className="d-flex align-items-center gap-2">
                <span className={`kb-status-badge status-${status}`}>{status}</span>
                <button
                  className="btn btn-outline-primary"
                  style={{ fontSize: 13, borderRadius: 8 }}
                  onClick={handleStatusButtonClick}
                  disabled={savingStatus}
                >
                  {savingStatus
                    ? "Updating..."
                    : status === "draft"
                      ? "Mark as Ready"
                      : "Revert to Draft"}
                </button>
                <span
                  title={status !== "draft" ? "Set to Draft first" : undefined}
                  style={{ display: "inline-block" }}
                >
                  <button
                    className="btn btn-outline-danger d-flex align-items-center gap-2"
                    style={{ fontSize: 13, borderRadius: 8 }}
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={status !== "draft" || deleting}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </span>
              </div>
            )}
          </div>
        </section>

        {docLoading ? (
          <div className="text-center text-muted py-5">Loading document...</div>
        ) : (
          <>
            {activeTab === "content" && (
              <div>
                <div className="kbd-content-header">
                  <div className="kbd-content-header-left">
                    <input
                      type="text"
                      className="form-control kbd-title-input"
                      placeholder="Enter document title..."
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      disabled={isLive}
                    />
                    <button className="btn btn-primary" onClick={handleSaveTitle} disabled={savingTitle || isLive}>
                      {savingTitle ? "Saving..." : "Save Title"}
                    </button>
                  </div>
                  <div className="kbd-content-header-right">
                    <button className="btn btn-primary" onClick={handleSaveContent} disabled={savingContent || isLive}>
                      {savingContent ? "Saving..." : "Save Content"}
                    </button>
                    <button
                      className="btn btn-outline-primary d-flex align-items-center gap-2"
                      onClick={handleReset}
                      disabled={isLive}
                    >
                      <RefreshCcw size={14} />
                      Reset
                    </button>
                  </div>
                </div>
                {isLive && (
                  <p className="kb-create-note">
                    <AlertTriangle size={14} />
                    This document is live and read-only. Revert to draft to edit it.
                  </p>
                )}
                <textarea
                  className="form-control kb-textarea"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  disabled={isLive}
                />
              </div>
            )}

            {activeTab === "chunks" && (
              <div>
                <div className="kbd-chunks-meta">
                  <span className="kbd-chunks-count">{chunks.length} chunks found</span>
                </div>
                {chunksLoading ? (
                  <div className="text-center text-muted py-5">Loading chunks...</div>
                ) : chunks.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    {status === "live"
                      ? "No chunks found."
                      : "This document hasn't been chunked yet."}
                  </div>
                ) : (
                  <div className="kb-chunk-grid">
                    {chunks.map((chunk, index) => (
                      <div className="kbd-chunk-card" key={chunk.chunkPid || index}>
                        {!chunk.vectorUpdated && (
                          <div className="kbd-chunk-badges">
                            <span className="kbd-vector-warning">
                              <AlertTriangle size={12} />
                              Not embedded yet
                            </span>
                          </div>
                        )}
                        <span className="kb-badge">{chunk.title || `Chunk ${index + 1}`}</span>
                        <p className="kb-chunk-content mt-2 mb-0">{chunk.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default KnowledgeBaseDetails;
